import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useDeleteUnitMutation } from "../../lib/apiSlice/unit/unitSlice.tsx";

// Modal component for deleting a unit
function DeleteUnitModal({ open, onClose, onDelete, unit, isDeleting }) {
    if (!open || !unit) return null;
    return (
        <div className="z-50 fixed inset-0 flex justify-center items-center bg-black bg-opacity-50">
            <div className="bg-white shadow-lg p-6 rounded-lg w-full max-w-sm">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold text-lg">Delete Unit</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-700">&times;</button>
                </div>
                <div className="mb-6">
                    {String(unit.status || '').toLowerCase() === 'vacant' ? (
                        <>
                            <p>Remove Unit <span className="font-semibold">{unit.house}</span> from the visible units list?</p>
                            <div className="bg-blue-50 mt-3 p-3 border border-blue-200 rounded text-blue-800 text-sm">
                                The unit will only be hidden. Its invoices, payments and occupancy history will be preserved.
                            </div>
                        </>
                    ) : (
                        <div className="bg-yellow-50 p-3 border border-yellow-300 rounded text-yellow-900 text-sm">
                            This unit has an active occupancy and cannot be deleted. End the active occupancy first.
                        </div>
                    )}
                </div>
                <div className="flex justify-end gap-2">
                    <button
                        className="bg-green-600 px-4 py-2 rounded text-white"
                        onClick={onClose}
                        disabled={isDeleting}
                    >
                        No
                    </button>
                    <button
                        className="bg-red-600 px-4 py-2 rounded text-white"
                        onClick={() => onDelete(unit)}
                        disabled={isDeleting}
                    >
                        {isDeleting ? 'Hiding...' : 'Hide Unit'}
                    </button>
                </div>
            </div>
        </div>
    );
}

// Modal component for sending a reminder
function ReminderModal({ open, onClose, unit }) {
    if (!open || !unit) return null;
    return (
        <div className="z-50 fixed inset-0 flex justify-center items-center bg-black bg-opacity-50">
            <div className="bg-white shadow-lg p-6 rounded-lg w-full max-w-sm">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold text-lg">Send Reminder</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-700">&times;</button>
                </div>
                <div className="mb-6">
                    Send rent reminder to <span className="font-semibold">{unit.tenant}</span> for <span className="font-semibold">{unit.house}</span>?
                </div>
                <div className="flex justify-end gap-2">
                    <button
                        className="bg-gray-300 px-4 py-2 rounded text-gray-800"
                        onClick={onClose}
                    >
                        Cancel
                    </button>
                    <button
                        className="bg-blue-600 px-4 py-2 rounded text-white"
                        onClick={onClose /* Replace with actual send logic */}
                    >
                        Send
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function HousesDisplay({ unitDetails, houseFilters, onHouseFilterChange }) {
    const [viewMode, setViewMode] = useState("card"); // "card" or "table"
    const [showModalDeleteHouse, setShowModalDeleteHouse] = useState(false);
    const [modalDeleteUnit, setModalDeleteUnit] = useState(null);
    const [showReminderModal, setShowReminderModal] = useState(false);
    const [reminderUnit, setReminderUnit] = useState(null);
    const [deleteUnit, { isLoading: isDeletingUnit }] = useDeleteUnitMutation();

    // Pagination state
    const pageSize = 12;
    const [currentPage, setCurrentPage] = useState(0);

    // Utility function to round up numbers
    function roundUp(value) {
        return Math.ceil(value);
    }

    function handleOpenModalDeleteHouse(unitDetail) {
        setModalDeleteUnit(unitDetail);
        setShowModalDeleteHouse(true);
    }
    function handleCloseModalDeleteHouse() {
        setShowModalDeleteHouse(false);
        setModalDeleteUnit(null);
    }
    async function handleDeleteHouse(unit) {
        if (!unit?.house_id) {
            toast.error('Unable to identify the unit to delete.');
            return;
        }
        try {
            const response = await deleteUnit(unit.house_id).unwrap();
            setShowModalDeleteHouse(false);
            setModalDeleteUnit(null);
            toast.success(response?.message || 'Unit hidden successfully.');
        } catch (error) {
            const message = error?.data?.error
                || error?.data?.detail
                || error?.error
                || 'Failed to hide the unit.';
            toast.error(message);
        }
    }

    function handleReminderModalOpen(unit) {
        setReminderUnit(unit);
        setShowReminderModal(true);
    }
    function handleReminderModalClose() {
        setShowReminderModal(false);
        setReminderUnit(null);
    }

    function handlePrevPage() {
        setCurrentPage((prev) => Math.max(prev - 1, 0));
    }
    function handleNextPage() {
        setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1));
    }

    // CSV Export utility
    function exportToCSV(data) {
        const replacer = (key, value) => value === null ? '' : value;
        const header = ["house", "tenant", "status", "house_rent", "rent_payment_status"];
        const csv = [
            header.join(','),
            ...data.map(row => header.map(fieldName => JSON.stringify(row[fieldName], replacer)).join(','))
        ].join('\r\n');

        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'units.csv';
        a.click();
        window.URL.revokeObjectURL(url);
    }

    // Enhanced filtering logic - used for both card and table views
    const filteredUnits = unitDetails?.filter(unit => {
        // Search filter
        const matchesSearch = 
            unit.house?.toLowerCase().includes(houseFilters.search.toLowerCase()) ||
            unit.tenant?.toLowerCase().includes(houseFilters.search.toLowerCase());
        
        // Rent amount filter
        const matchesRentAmount = 
            !houseFilters.rentAmount || 
            (unit.house_rent === 0 
                ? unit.service_charge === parseFloat(houseFilters.rentAmount)
                : unit.house_rent === parseFloat(houseFilters.rentAmount));
        
        // Payment status filter
        const matchesPaymentStatus = 
            !houseFilters.paymentStatus || 
            unit.rent_payment_status === houseFilters.paymentStatus;
        
        // Occupancy status filter
        const matchesStatus = 
            houseFilters.status === "all" ||
            (houseFilters.status === "vacant" && unit.status === "vacant") ||
            (houseFilters.status === "occupied" && unit.status !== "vacant");
        
        return matchesSearch && matchesRentAmount && matchesPaymentStatus && matchesStatus;
    }) || [];

    // Pagination for filtered data
    const startIndex = currentPage * pageSize;
    const endIndex = Math.min(startIndex + pageSize, filteredUnits.length);
    const pagedUnits = filteredUnits.slice(startIndex, endIndex);
    const totalPages = Math.ceil(filteredUnits.length / pageSize);

    return (
        <div>
            {/* Toggle Button */}
            <div className="flex justify-end mb-3">
                <button
                    className={`mr-2 px-3 py-1 rounded border text-sm ${viewMode === "card" ? "bg-blue-600 text-white" : "bg-white text-blue-600 border-blue-600"}`}
                    onClick={() => setViewMode("card")}
                    disabled={viewMode === "card"}
                >
                    Card View
                </button>
                <button
                    className={`px-3 py-1 rounded border text-sm ${viewMode === "table" ? "bg-gray-700 text-white" : "bg-white text-gray-700 border-gray-700"}`}
                    onClick={() => setViewMode("table")}
                    disabled={viewMode === "table"}
                >
                    Table View
                </button>
            </div>

            {!unitDetails ? (
                <div className='py-8 text-gray-500 text-center noHouse'>You have no units in this property</div>
            ) : viewMode === "card" ? (
                <div>
                    <div className="gap-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                        {pagedUnits.map((unitDetail, index) => (
                            <div
                                className="relative flex flex-col bg-white shadow-md hover:shadow-lg p-4 border border-gray-100 rounded-lg transition-shadow"
                                key={unitDetail.house_id || index}
                            >
                                {/* Image with price badge */}
                                <Link to={`/selected-house/${unitDetail.house_id}`} className="relative mb-0">
                                    <div className="relative mb-3">
                                        <img
                                            src={unitDetail.house_image}
                                            className="rounded w-full h-44 object-cover"
                                            alt="Unit"
                                        />
                                        <div className="top-2 left-2 absolute bg-white shadow px-2 py-1 border border-black/10 rounded font-bold text-black text-xs">
                                            Ksh. {unitDetail.house_rent?.toLocaleString()} /month
                                        </div>
                                    </div>
                                </Link>
                                {/* Unit Number */}
                                <div className="mb-0 font-bold text-gray-400 text-xl leading-none">
                                    {unitDetail.house}
                                </div>
                                {/* Tenant Name */}
                                <div className="mb-3 font-bold text-black text-base">
                                    {unitDetail.tenant}
                                </div>
                                {/* Status Badges */}
                                <div className="flex gap-2 mb-4">
                                    {unitDetail.status === "vacant" ? (
                                        <span className="bg-green-600 p-2 rounded-full text-white text-sm">
                                            Vacant
                                        </span>
                                    ) : (
                                        <>
                                            <span className="bg-yellow-400 p-2 rounded-full text-white text-sm">
                                                Occupied
                                            </span>
                                            {unitDetail.rent_payment_status === "paid" ? (
                                                <span className="bg-green-500 p-2 rounded-full text-white text-sm">{unitDetail.rent_payment_status}</span>
                                            ) : unitDetail.rent_payment_status === "partial" ? (
                                                <span className="bg-gray-400 p-2 rounded-full text-white text-sm">{unitDetail.rent_payment_status}</span>
                                            ) : unitDetail.rent_payment_status === "unpaid" ? (
                                                <span className="bg-red-500 p-2 rounded-full text-white text-sm">{unitDetail.rent_payment_status}</span>
                                            ) : (
                                                ""
                                            )}

                                        </>
                                    )}


                                </div>
                                {/* Action Button */}
                                {unitDetail.rent_payment_status === 'unpaid' && !unitDetail.status === 'vacant' &&(
                                    <button
                                        onClick={() => handleReminderModalOpen(unitDetail)}
                                        className="flex items-center gap-1 mb-2 font-medium text-red-600 hover:text-red-700 text-base"
                                    >
                                        Send Reminder <span>&rarr;</span>
                                    </button>
                                )}

                                {/* Trash Icon */}
                                <button
                                    onClick={() => handleOpenModalDeleteHouse(unitDetail)}
                                    className="right-4 bottom-4 absolute bg-red-400 hover:bg-red-600 p-2 rounded text-white"
                                    title="Delete"
                                >
                                    <i className="bi bi-trash"></i>
                                </button>
                            </div>
                        ))}
                    </div>
                    {/* Pagination for card view */}
                    <div className="flex justify-center items-center gap-4 py-8">
                        <button
                            onClick={handlePrevPage}
                            disabled={currentPage === 0}
                            className="bg-gray-100 disabled:opacity-50 px-3 py-1 border rounded text-gray-700"
                        >
                            &lt;
                        </button>
                        <span className="text-gray-700 text-sm">{`${startIndex + 1}-${endIndex} of ${filteredUnits.length || 0}`}</span>
                        <button
                            onClick={handleNextPage}
                            disabled={currentPage === totalPages - 1 || totalPages === 0}
                            className="bg-gray-100 disabled:opacity-50 px-3 py-1 border rounded text-gray-700"
                        >
                            &gt;
                        </button>
                    </div>
                </div>
            ) : (
                // Table View
                <div className="bg-white shadow-md p-4 border border-gray-100 rounded-lg">
                    {/* Search, Filter, Export Controls */}
                    <div className="flex md:flex-row flex-col md:justify-between md:items-center gap-2 mb-4">
                        <div className="flex gap-2">
                            <input
                                type="text"
                                placeholder="Search.."
                                className="px-3 py-1 border rounded text-sm"
                                value={houseFilters.search}
                                onChange={e => { 
                                    onHouseFilterChange({ ...houseFilters, search: e.target.value });
                                    setCurrentPage(0);
                                }}
                            />
                        </div>
                        <button
                            className="bg-green-600 px-4 py-1 rounded font-semibold text-md text-white"
                            onClick={() => exportToCSV(filteredUnits)}
                        >
                            Export CSV
                        </button>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="bg-white min-w-full">
                            <thead>
                                <tr className="">
                                    <th className="p-2 border-b font-bold text-gray-700 text-md text-left">Image</th>
                                    <th className="p-2 border-b font-bold text-gray-700 text-md text-left">House</th>
                                    <th className="p-2 border-b font-bold text-gray-700 text-md text-left">Tenant</th>
                                    <th className="p-2 border-b font-bold text-gray-700 text-md text-left">Status</th>
                                    <th className="p-2 border-b font-bold text-gray-700 text-md text-left">Rent</th>
                                    <th className="p-2 border-b font-bold text-gray-700 text-md text-left">Payment Status</th>
                                    <th className="p-2 border-b font-bold text-gray-700 text-md text-left">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pagedUnits.map((unitDetail, index) => (
                                    <tr key={unitDetail.house_id || index} className="hover:bg-gray-50">
                                        <td className="p-2 border-b">
                                            <img src={unitDetail.house_image} alt="img" className="rounded w-20 h-12 object-cover" />
                                        </td>
                                        <td className="p-2 border-b">
                                            <Link to={`/selected-house/${unitDetail.house_id}`}>
                                                {unitDetail.house}
                                            </Link>

                                        </td>
                                        <td className="p-2 border-b capitalize">{unitDetail.tenant?.toLowerCase()}</td>
                                        <td className="p-2 border-b">
                                            {unitDetail.status === "vacant" ? (
                                                <span className="bg-green-600 p-2 rounded-lg text-white text-sm">Vacant</span>
                                            ) : (
                                                <span className="bg-yellow-500 p-2 rounded-lg text-white text-sm">Occupied</span>
                                            )}
                                        </td>
                                        <td className="p-2 border-b">
                                            {unitDetail.house_rent === 0
                                                ? `Ksh. ${roundUp(unitDetail.service_charge)?.toLocaleString()} `
                                                : unitDetail.house_rent > 0 && unitDetail.booking_value > 0
                                                    ? `Ksh. ${roundUp((unitDetail.house_rent || 0) * parseInt(unitDetail.rent_period || 1))} /${unitDetail.rent_period_name}`
                                                    : unitDetail.house_rent > 0
                                                        ? `Ksh. ${roundUp(unitDetail.house_rent)?.toLocaleString()} /month`
                                                        : ""}
                                        </td>
                                        <td className="p-2 border-b">
                                            {unitDetail.rent_payment_status === "paid" ? (
                                                <span className="bg-green-500 p-2 rounded-lg text-white text-sm">{unitDetail.rent_payment_status}</span>
                                            ) : unitDetail.rent_payment_status === "partial" ? (
                                                <span className="bg-gray-400 p-2 rounded-lg text-white text-sm">{unitDetail.rent_payment_status}</span>
                                            ) : unitDetail.rent_payment_status === "unpaid" ? (
                                                <span className="bg-red-500 p-2 rounded-lg text-white text-sm">{unitDetail.rent_payment_status}</span>
                                            ) : (
                                                ""
                                            )}
                                        </td>
                                        <td className="p-2 border-b">
                                            <button
                                                onClick={() => handleOpenModalDeleteHouse(unitDetail)}
                                                className="bg-red-600 hover:bg-red-700 p-2 rounded-lg text-white"
                                            >
                                                <i className="bi bi-trash"></i>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {/* Pagination for table */}
                    <div className="flex justify-center items-center gap-4 py-8">
                        <button
                            onClick={handlePrevPage}
                            disabled={currentPage === 0}
                            className="bg-gray-100 disabled:opacity-50 px-3 py-1 border rounded text-gray-700"
                        >
                            &lt;
                        </button>
                        <span className="text-gray-700 text-sm">{`${startIndex + 1}-${endIndex} of ${filteredUnits.length}`}</span>
                        <button
                            onClick={handleNextPage}
                            disabled={currentPage === totalPages - 1 || totalPages === 0}
                            className="bg-gray-100 disabled:opacity-50 px-3 py-1 border rounded text-gray-700"
                        >
                            &gt;
                        </button>
                    </div>
                </div>
            )}

            {/* Modals */}
            <DeleteUnitModal
                open={showModalDeleteHouse}
                onClose={handleCloseModalDeleteHouse}
              onDelete={handleDeleteHouse}
              unit={modalDeleteUnit}
              isDeleting={isDeletingUnit}
            />
            <ReminderModal
                open={showReminderModal}
                onClose={handleReminderModalClose}
                unit={reminderUnit}
            />
        </div>
    );
}
